import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { flowRight, template } from "lodash";
import {
    ButtonToolbar,
    Form,
    Heading,
    IconButton,
    Message,
    toaster,
    Tooltip,
    Whisper,
} from "rsuite";

import EditIcon from "@rsuite/icons/Edit";
import TrashIcon from "@rsuite/icons/Trash";

import withError, { WithErrorProps } from "hocs/withError/withError";
import withLoader, { WithLoaderProps } from "hocs/withLoader/withLoader";

import useRefreshPage from "hooks/useRefreshPage";

import constants from "utils/constants";

import { getErrorMessage } from "api/defaults";
import {
    deleteEmailTemplate,
    loadEmailTemplates,
    updateEmailTemplate,
} from "api/deliverables/email-templates-api";

import { IEmailTemplate } from "store/deliverables/email-templates/email-templates-types";

import { AppDispatch } from "store/store";

import {
    setManageTemplateDetails,
    setManageTemplateOpened,
} from "store/deliverables/email-templates/email-templates-actions";

import ConfirmModal, {
    ConfirmationMode,
} from "components/Common/ConfirmModal/ConfirmModal";
import Overflow from "components/Common/Overflow/Overflow";

import ManageEmailTemplate from "./ManageEmailTemplate/ManageEmailTemplate";

import css from "./EmailTemplates.module.css";

interface ComponentProps extends WithErrorProps, WithLoaderProps {}

const useAppDispatch = () => useDispatch<AppDispatch>();

const EmailTemplates: React.FC<ComponentProps> = ({
    showLoader,
    showError,
}: ComponentProps) => {
    const dispatch = useAppDispatch();
    const [emailTemplates, setEmailTemplates] = useState<IEmailTemplate[]>([]);
    const [isEmailTemplatesLoaded, setIsEmailTemplatesLoaded] =
        useState<boolean>(false);
    const [deleteConfirmVisible, setDeleteConfirmVisible] = useState(false);
    const [currentTemplate, setCurrentTemplate] = useState<IEmailTemplate>();
    const containerRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    const title = t("deliverables.emailTemplates.title");

    const fetchEmailTemplates = () => {
        showError("");
        showLoader(true);
        loadEmailTemplates().then(([payload, status]) => {
            showLoader(false);
            if (payload && status === constants.statusCode.OK) {
                setEmailTemplates(payload);
            } else {
                showError(getErrorMessage(String(payload), status));
            }
        });
        setIsEmailTemplatesLoaded(true);
    };

    const handleCancel = () => {
        setDeleteConfirmVisible(false);
    };

    const handleConfirm = async () => {
        if (currentTemplate) {
            showLoader(true);
            deleteEmailTemplate(currentTemplate.id).then(
                ([payload, status]) => {
                    showLoader(false);
                    setIsEmailTemplatesLoaded(false);
                    if (status === constants.statusCode.OK) {
                        toaster.push(
                            <Message showIcon type="success">
                                {template(
                                    t(
                                        "deliverables.emailTemplates.deleteSuccessMessage"
                                    )
                                )({
                                    templateName: currentTemplate.name,
                                })}
                            </Message>,
                            {
                                duration: constants.errorAlertDurationMsec,
                            }
                        );
                    } else {
                        const errorMessage = getErrorMessage(
                            String(payload),
                            status
                        );
                        toaster.push(
                            <Message type="success" showIcon>
                                {errorMessage}
                            </Message>,
                            {
                                duration: constants.errorAlertDurationMsec,
                            }
                        );
                    }
                }
            );
        }
        setDeleteConfirmVisible(false);
    };

    const handleEditIconClick = (item: IEmailTemplate) => {
        dispatch(setManageTemplateOpened(true));
        dispatch(
            setManageTemplateDetails({
                title: t("deliverables.emailTemplates.modal.editTitle"),
                confirmButtonName: t("deliverables.emailTemplates.modal.save"),
                actionName: t("deliverables.emailTemplates.updatedAction"),
                emailTemplate: item,
                apiMethod: updateEmailTemplate,
            })
        );
    };

    useRefreshPage(() => {
        containerRef.current?.scrollTo(0, 0);
        fetchEmailTemplates();
    });

    useEffect(() => {
        if (!isEmailTemplatesLoaded) {
            fetchEmailTemplates();
        }
    }, [isEmailTemplatesLoaded, fetchEmailTemplates]);

    const tableRows =
        emailTemplates &&
        emailTemplates.length &&
        emailTemplates.map((item, index) => {
            return (
                <tr
                    className={css.tableRowTemplates}
                    key={String("rowIndex_" + index)}
                >
                    <td className={css.cellTemplates}>
                        <Overflow>{item.name}</Overflow>
                    </td>
                    <td className={css.cellTemplates}>{item.locale}</td>
                    <td className={css.cellTemplates}>
                        <ButtonToolbar block>
                            <Whisper
                                trigger={"hover"}
                                placement={"top"}
                                delay={constants.defaultDelay}
                                speaker={
                                    <Tooltip>
                                        {t(
                                            "deliverables.emailTemplates.table.tooltip.edit"
                                        )}
                                    </Tooltip>
                                }
                            >
                                <IconButton
                                    icon={
                                        <EditIcon
                                            onClick={() => {
                                                handleEditIconClick(item);
                                            }}
                                        ></EditIcon>
                                    }
                                />
                            </Whisper>
                            <Whisper
                                trigger={"hover"}
                                placement={"top"}
                                delay={constants.defaultDelay}
                                speaker={
                                    <Tooltip>
                                        {t(
                                            "deliverables.emailTemplates.table.tooltip.delete"
                                        )}
                                    </Tooltip>
                                }
                            >
                                <IconButton
                                    icon={
                                        <TrashIcon
                                            onClick={() => {
                                                setDeleteConfirmVisible(true);
                                                setCurrentTemplate(item);
                                            }}
                                        ></TrashIcon>
                                    }
                                />
                            </Whisper>
                        </ButtonToolbar>
                    </td>
                </tr>
            );
        });

    return (
        <>
            <ConfirmModal
                onCancel={handleCancel}
                onConfirm={handleConfirm}
                open={deleteConfirmVisible}
                message={template(
                    t("deliverables.emailTemplates.confirmationMessage")
                )({ templateName: currentTemplate?.name })}
                mode={ConfirmationMode.Destructive}
            />
            <ManageEmailTemplate
                refreshTemplates={fetchEmailTemplates}
                existingTemplates={emailTemplates}
            />
            {isEmailTemplatesLoaded && (
                <div className={css.container}>
                    <div className={css.emailTemplatesContent}>
                        <Heading level={1}>{title}</Heading>

                        <table className={css.templatesTable}>
                            <thead>
                                <tr className={css.tableHeaderTemplates}>
                                    <th className={css.cellTemplates}>
                                        {t(
                                            "deliverables.emailTemplates.table.header.name"
                                        )}
                                        <Form.HelpText
                                            tooltip
                                            className={css.help}
                                        >
                                            {t(
                                                "deliverables.emailTemplates.table.tooltip.name"
                                            )}
                                        </Form.HelpText>
                                    </th>
                                    <th className={css.cellTemplates}>
                                        {t(
                                            "deliverables.emailTemplates.table.header.locale"
                                        )}
                                        <Form.HelpText
                                            tooltip
                                            className={css.help}
                                        >
                                            {t(
                                                "deliverables.emailTemplates.table.tooltip.locale"
                                            )}
                                        </Form.HelpText>
                                    </th>
                                    <th className={css.cellTemplates}>
                                        {t(
                                            "deliverables.emailTemplates.table.header.action"
                                        )}
                                    </th>
                                </tr>
                            </thead>
                            <tbody>{tableRows || []}</tbody>
                        </table>
                    </div>
                </div>
            )}
        </>
    );
};

const Component = flowRight(withLoader, withError)(EmailTemplates);

export default Component;
